import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { isEmpty, get, isEqual } from 'lodash';

import routes from '../../routes';
import {
  exceptionComponentPageTypes,
  cashCardAPIParams,
  apiResponseCodes,
  serverErrorCodes,
} from '../../constants/globals';
import { constants } from '../../constants/configuration';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import { extractWhitelabelSubdomain, generateUUID, isIOS, toTitleCase } from '../../utils/helpers';
import {
  fetchGuestCashCardsAction,
  clearApiErrorResponseAction,
  beginAjaxCallAction,
  endAjaxCallAction,
} from '../../actions/app.action';
import {
  dispatchGuestLoginDataAction,
  signInByTrueCallerAction,
} from '../../actions/authentication';
import {
  getButtonHandlerCta,
  getErrorDescription,
  getErrorTitle,
  getTrueCallerDeepLink,
  getTrueCallerErrorContent,
} from '../../selectors';

import './CashCards.scss';

const Header = asyncRoute(() => import('../../components/Header'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const CashCardPreview = asyncRoute(() => import('../../components/Card/CashCardPreview'));
const AuthenticationPopup = asyncRoute(() => import('../../components/Popup/AuthenticationPopup'));
const AuthenticationScreen = asyncRoute(() => import('../../components/Authentication/Screen'));
const CashCardDetail = asyncRoute(() => import('./Detail'));

const guestCashCardsListResources = constants.cashCard.guestCashCardsList;
const authenticationResources = constants.authentication;

class CashCards extends Component {
  constructor(props) {
    super(props);
    this.state = {
      guestCashCards: [],
      showAuthenticationScreen: false,
      showAuthenticationPopup: false,
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      errorPopupCancellable: true,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      initialAPIInteractionDone: false, // Whether the API calls on mount have completed (irrespective of SUCCESS or ERROR response)
      toast: '',
    };
  }

  componentDidMount() {
    const guestId = get(this.props, 'loginData.id', '');

    // If the guest is not logged-in, authenticate the guest first
    if (isEmpty(guestId)) {
      this.setShowAuthenticationScreen(true);
    } else {
      this.getGuestCashCards(guestId);
    }

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  componentDidUpdate(prevProps) {
    const currentLoginData = this.props.loginData;
    const previousLoginData = prevProps.loginData;

    // Upon login after the app load
    if (!isEmpty(currentLoginData) && !isEqual(currentLoginData, previousLoginData)) {
      this.setShowAuthenticationPopup(false);
      this.setShowAuthenticationScreen(false);

      const guestId = get(currentLoginData, 'id', '');

      this.getGuestCashCards(guestId);
    }

    // Check for error response
    if (
      !isEmpty(get(this.props, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(prevProps, 'errorResponse'))
    ) {
      const { code, message } = this.props.errorResponse;

      this.setState({
        showErrorPopup: true,
        errorContent: {
          title: getErrorTitle({ source: guestCashCardsListResources, code }),
          description: getErrorDescription({
            source: guestCashCardsListResources,
            code,
            message,
          }),
          buttonCta: getButtonHandlerCta({ source: guestCashCardsListResources, code }),
        },
        handleErrorCTA: this.getErrorCTAHandler(code),
      });
    }
  }

  getGuestCashCards = (guestId) => {
    const {
      restaurant,
      isRestaurantEntityGroup,
      restaurantGroup: { restaurants },
      fetchGuestCashCards,
    } = this.props;

    let restaurantIds = [];
    const subDomain = extractWhitelabelSubdomain();

    // Bring only whitelabel-scope entries if it's a whitelabel subdomain visit
    if (subDomain) {
      if (typeof isRestaurantEntityGroup === 'boolean' && isRestaurantEntityGroup) {
        restaurantIds = !isEmpty(restaurants)
          ? restaurants.map((restaurantObj) => get(restaurantObj, 'id', ''))
          : [];
      } else {
        restaurantIds.push(get(restaurant, 'id', ''));
      }
    }

    const pageSize = cashCardAPIParams.guestCashCards.pageSize;
    const sortType = cashCardAPIParams.guestCashCards.sortType;

    const constructedURL = `/cashCards/guests?guestId=${guestId}${
      restaurantIds.length > 0 ? `&restaurantIds=${restaurantIds}` : ''
    }&size=${pageSize}&sort=${sortType}`;

    fetchGuestCashCards({ url: constructedURL }).then((response) => {
      // As the API call on mount has completed, mark the initialAPIInteractionDone as true
      this.setInitialAPIInteractionDone(true);

      // On API call success, populate guestCashCards state with guestCashCards prop
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const { guestCashCards } = this.props;
        this.setGuestCashCards(guestCashCards);
      }
    });
  };

  handleAuthenticationScreenCTAClick = () => {
    this.authenticateGuest();
  };

  authenticateGuest = () => {
    const { appSettings, restaurantConfigAdditionalProps } = this.props;
    // first check at restaurant level then check at app level
    const isTrueCallerEnabled = get(
      restaurantConfigAdditionalProps.find((prop) => get(prop, 'code') === 'tc.isEnabled'),
      'value',
      get(appSettings, 'tc.isEnabled', 'false'),
    );

    if (JSON.parse(isTrueCallerEnabled) && !isIOS()) {
      const requestId = generateUUID();
      const trueCallerDeeplink = getTrueCallerDeepLink({ requestId });
      window.location = trueCallerDeeplink;

      setTimeout(() => {
        if (document.hasFocus()) {
          // Truecaller app not present on the device, initiate the regular OTP authentication flow
          this.setShowAuthenticationPopup(true);
        } else {
          // Truecaller app present on the device and the profile overlay opens
          this.handleTrueCallerSignIn({ requestId });
        }
      }, 600);
    } else {
      this.setShowAuthenticationPopup(true);
    }
  };

  handleTrueCallerSignIn = ({ requestId }) => {
    this.props.beginAjaxCall();

    this.props.signInByTrueCaller({ requestId }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const guestLoginData = get(response, 'data', {});
        this.setState(
          {
            toast: authenticationResources.toastMessage.successfulLogin,
          },
          () => {
            this.props.dispatchGuestLoginData(guestLoginData);
            this.props.endAjaxCall();
          },
        );
      } else {
        const code = get(response, 'data.code', '');
        const message = toTitleCase(get(response, 'data.message', ''));

        if (
          code === authenticationResources.userRejected ||
          code === authenticationResources.trueCallerTimeout ||
          code === authenticationResources.trueCallerRequestError
        ) {
          let errorContent = getTrueCallerErrorContent({
            code,
            message,
          });
          this.setState(
            {
              showErrorPopup: true,
              errorContent,
              ...(code === authenticationResources.userRejected
                ? { handleErrorCTA: this.handleTrueCallerExceptionCTA }
                : {}),
            },
            () => {
              this.props.endAjaxCall();
            },
          );
        } else if (code === authenticationResources.requestIdNotFound) {
          // Again hit the true caller sing in handler
          this.handleTrueCallerSignIn({ requestId });
        } else {
          this.setState(
            {
              showAuthenticationPopup: true,
            },
            () => {
              this.props.endAjaxCall();
            },
          );
        }
      }
    });
  };

  handleTrueCallerExceptionCTA = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
    });

    this.authenticateGuest();
  };

  handleAuthenticationPopupClose = () => {
    this.setShowAuthenticationPopup(false);
  };

  handleCashCardClick = (guestCashCardId) => {}; // TODO: Check if this handler's target event needs to be handled

  handleViewCashCardDetailsClick = (guestCashCardId) => {
    const { history, match } = this.props;
    history && match && history.push(`${match.path}/${guestCashCardId}`);
  };

  onBackClick = () => {
    const { history, parentRoute } = this.props;
    if (isEmpty(parentRoute)) {
      history.push('/'); // Back to top-level route (which will redirect to contextual home)
    } else {
      history.push(`${parentRoute}`);
    }
  };

  onViewMoreItemsClick = () => {
    const { guestCashCardsLinksMetadata } = this.props;
    let url = get(guestCashCardsLinksMetadata, 'next.href', '');

    this.props.fetchGuestCashCards({ url }).then((response) => {
      // On API call success, populate guestCashCards state with guestCashCards prop
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const { guestCashCards: nextGuestCashCards } = this.props;
        const { guestCashCards: prevGuestCashCards } = this.state;
        const cumulativeGuestCashCards = [...prevGuestCashCards, ...nextGuestCashCards]; // Append guest cards into state when new guest cash cards are loaded via view more
        this.setGuestCashCards(cumulativeGuestCashCards);
      }
    });
  };

  onCloseExceptionComponent = () => {
    this.setState(
      {
        showErrorPopup: false,
        errorContent: {
          title: '',
          description: '',
          buttonCta: '',
        },
      },
      () => this.props.clearApiErrorResponse(),
    );
  };

  getErrorCTAHandler = (code) => {
    switch (code) {
      case serverErrorCodes.invalidMethodParams:
        return () => window.location.reload();
      case serverErrorCodes.unknownServerError:
        return this.onCloseExceptionComponent;
      default:
        return this.onBackClick;
    }
  };

  setGuestCashCards = (guestCashCards) => {
    this.setState({
      guestCashCards: [...guestCashCards],
    });
  };

  setInitialAPIInteractionDone = (flag) => {
    this.setState({
      initialAPIInteractionDone: flag,
    });
  };

  setShowAuthenticationPopup = (flag) => {
    this.setState({
      showAuthenticationPopup: flag,
    });
  };

  setShowAuthenticationScreen = (flag) => {
    this.setState({
      showAuthenticationScreen: flag,
    });
  };

  componentWillUnmount() {
    this.props.clearApiErrorResponse();
  }

  render() {
    const { match, guestCashCardsPageMetadata, errorResponse, loginData } = this.props;
    const {
      guestCashCards,
      showAuthenticationPopup,
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      errorPopupCancellable,
      errorPopupStyleType,
      initialAPIInteractionDone,
      showAuthenticationScreen,
    } = this.state;

    const guestId = get(loginData, 'id', '');

    const totalPages = get(guestCashCardsPageMetadata, 'totalPages', 0);
    const currentPageNumber = get(guestCashCardsPageMetadata, 'number', 0);

    return (
      <>
        <Switch>
          {guestId && (
            <Route
              path={`${match.path}/:guestCashCardId`}
              render={(props) => <CashCardDetail {...props} errorResponse={errorResponse} />}
            />
          )}
          <Route
            exact
            path={match.path}
            render={(props) => (
              <div className="guest-cash-cards">
                {initialAPIInteractionDone && (
                  <>
                    <Header
                      onBackClick={this.onBackClick}
                      identifier={routes.containers.cashCards}
                    />
                    {guestCashCards && guestCashCards.length > 0 ? (
                      <div className="guest-cash-cards-inner-wrapper">
                        {guestCashCards.map((card, index) => (
                          <div
                            key={get(card, 'id', index)}
                            className="cash-card-preview-outer-wrapper"
                          >
                            <CashCardPreview
                              cardName={get(card, 'cashCard.title', '')}
                              balancePoints={get(card, 'balance', '')}
                              backgroundColor={get(card, 'cashCard.colour', '')}
                              brandLogo={get(card, 'cashCard.iconUrl', '')}
                              isBackgroundBlended={get(card, 'cashCard.isBgBlend', false)}
                              onCardClick={() => this.handleCashCardClick(get(card, 'id', ''))}
                              onViewCardDetailsClick={() =>
                                this.handleViewCashCardDetailsClick(get(card, 'id', ''))
                              }
                            />
                          </div>
                        ))}
                        {currentPageNumber < totalPages && (
                          <div
                            className="guest-cash-cards_view-more"
                            onClick={this.onViewMoreItemsClick}
                          >
                            {guestCashCardsListResources.viewMore}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="guest-cash-cards_empty-results">
                        <div className="empty-results-label">
                          {guestCashCardsListResources.noCashCard}
                        </div>
                        <div className="back-to-menu" onClick={this.onBackClick}>
                          {guestCashCardsListResources.backToMenuLink}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          />
        </Switch>
        {showAuthenticationScreen && (
          <AuthenticationScreen
            className="guest-cash-cards_authentication-screen"
            handleCTAClick={this.handleAuthenticationScreenCTAClick}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={errorPopupCancellable ? this.onCloseExceptionComponent : undefined} // Sending this prop as undefined as the SegmentComponent being used internally in ExceptionComponent implicitly handles it
            title={get(errorContent, 'title', '')}
            description={get(errorContent, 'description', '')}
            buttonCta={get(errorContent, 'buttonCta', '')}
            handleButtonCta={handleErrorCTA}
          />
        )}
        {showAuthenticationPopup && (
          <AuthenticationPopup
            openSegmentPopup={showAuthenticationPopup}
            dismissSegmentPopup={this.handleAuthenticationPopupClose}
          />
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    loginData: get(state, 'authentication.loginData', {}),
    appSettings: get(state, 'app.masterData.appSettings', {}),
    parentRoute: get(state, 'app.parentRoute', ''),
    restaurant: get(state, 'app.restaurant', {}),
    restaurantGroup: get(state, 'app.restaurantGroup', {}),
    guestCashCards: get(state, 'app.guestCashCards._embedded.guestCashCards', []),
    guestCashCardsPageMetadata: get(state, 'app.guestCashCards.page', {}),
    guestCashCardsLinksMetadata: get(state, 'app.guestCashCards._links', {}),
    errorResponse: get(state, 'app.errorResponse', {}),
    restaurantConfigAdditionalProps: get(
      state,
      'app.restaurant.restaurantConfig.additionalProperties',
      [],
    ),
  }),
  {
    fetchGuestCashCards: fetchGuestCashCardsAction,
    clearApiErrorResponse: clearApiErrorResponseAction,
    beginAjaxCall: beginAjaxCallAction,
    endAjaxCall: endAjaxCallAction,
    signInByTrueCaller: signInByTrueCallerAction,
    dispatchGuestLoginData: dispatchGuestLoginDataAction,
  },
)(CashCards);
